import React, { Fragment } from 'react'
import classNames from 'classnames'
import { Field } from 'react-final-form'
import DatePicker from 'react-datepicker'
import { RefProvider, StateProvider, viewportContainer } from '../../core'

import './fields.scss'

export const VALIDATORS = {
  required: val => val ? undefined : 'Required',
  email: (val = '') => (
    val.match(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/)
      ? undefined
      : 'Invalid Format'
  ),
  phone: (val = '') => (
    val.match(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/)
      ? undefined
      : 'Invalid Format'
  ),
}

export const Row = ({ children }) => (
  <div styleName='row'>
    {children}
  </div>
)

export const Error = ({ children }) => (
  <div styleName="form-error">
    <h6>{children}</h6>
  </div>
)

export const InputField = ({ 
  name, 
  label, 
  placeholder,
  isRequired, 
  validators = [],
  hasValidationErrors,
  ...rest
}) => {
  const allValidators = isRequired ? [VALIDATORS.required, ...validators] : validators

  const validate = val => {
    const firstError = allValidators.find(v => v && !!v(val))
    return firstError
      ? firstError(val)
      : undefined
  }
  
  return (
    <Field 
      name={name} 
      validate={validate}
      render={({ 
        meta, 
        input, 
      }) => {

        const hasError = (meta.touched || hasValidationErrors) && meta.error

        const styleName = classNames(
          'input-field',
          (meta.active || input.value) && 'is-active',
          hasError && 'has-error'
        )

        return (
          <div styleName={`${styleName}`}>
            <label>{label}</label>
            <input 
              {...input}
              {...rest}
              placeholder={placeholder}
            ></input>
            {hasError && <span data-error styleName="error">{meta.error}</span>}
          </div>
        )
      }}
    />
  )
}


export const DateField = ({ 
  name, 
  label, 
  placeholder,
  isRequired, 
  validators = [],
  hasValidationErrors,
  ...rest
}) => {
  const allValidators = isRequired ? [VALIDATORS.required, ...validators] : validators

  const validate = val => {
    const firstError = allValidators.find(v => v && !!v(val))
    return firstError
      ? firstError(val)
      : undefined
  }
  
  return (
    <Field 
      name={name} 
      validate={validate}
      render={({ 
        meta, 
        input, 
      }) => {
        const hasError = (meta.touched || hasValidationErrors) && meta.error

        const styleName = classNames(
          'input-field',
          (meta.active || input.value) && 'is-active',
          hasError && 'has-error'
        )

        return (
          <div styleName={`${styleName}`}>
            <label>{label}</label>

            {viewportContainer.vw < 768 && (
              <input 
                {...input}
                {...rest}
                type="date"
              ></input>
            )}

            {viewportContainer.vw >= 768 && (
              <DatePicker
                minDate={new Date()}
                className={styleName}
                selected={input.value}
                onChange={input.onChange}
                onFocus={input.onFocus}
                onBlur={input.onBlur}
              />
            )}
            
            {hasError && <span data-error styleName="error">{meta.error}</span>}
          </div>
        )
      }}
    />
  )
}


export const DropdownField = ({ 
  name, 
  label, 
  isRequired, 
  options = [],
  validators = [],
  hasValidationErrors,
  onChange = () => {},
  ...rest
}) => {
  const allValidators = isRequired ? [VALIDATORS.required, ...validators] : validators

  const validate = val => {
    const firstError = allValidators.find(v => v && !!v(val))
    return firstError
      ? firstError(val)
      : undefined
  }
  
  return (
    <Field 
      name={name} 
      validate={validate}
      render={({ 
        meta, 
        input, 
      }) => {
        const hasError = (meta.touched || hasValidationErrors) && meta.error

        const styleName = classNames(
          'dropdown-field',
          (meta.active || input.value) && 'is-active',
          hasError && 'has-error'
        )

        const subcategories = {}
        const withoutSub = []
        options.forEach(o => {
          if (!o.subcategory) {
            withoutSub.push(o)
            return
          }
  
          subcategories[o.subcategory] = subcategories[o.subcategory] || []
          subcategories[o.subcategory].push(o)
        })

        const getOptions = (opts) => (
          opts.map((opt, i) => {
            const onClick=() => {
              input.onChange(opt.value)
              onChange(opt)
            }

            return (
              <div styleName='option' key={`${i}-${opt.name}`} onMouseDown={onClick}>
                <p>{opt.name}</p>
              </div>
            )
          })
        )

        const renderOptions = () => {
          const subs = Object.keys(subcategories)
          if (subs.length < 1) {
            return getOptions(options)
          }

          return (
            <Fragment>
              {getOptions(withoutSub)}
              <div style={{ height: '2rem' }} />
              {subs.map((sub, i) => {
                const arr = subcategories[sub]
                return (
                  <div styleName='subcategory' key={i}>
                    <h6>
                      {sub}
                    </h6>
                    {getOptions(arr)}
                  </div>
                )
              })}
            </Fragment>
          )
        }

        const renderSelectOptions = () => {
          const subs = Object.keys(subcategories)

          const rend = m => (
            m.map((opt, i) => (
              <option key={i} value={opt.value}>{opt.name}</option>
            ))
          )

          if (subs.length < 1) {
            return rend(options)
          }

          return (
            <Fragment>
              {rend(withoutSub)}
              {subs.map((sub, i) => {
                const arr = subcategories[sub]
                return (
                  <Fragment key={i}>
                    <option disabled value="">{sub}</option>
                    {rend(arr)}
                  </Fragment>
                )
              })}
            </Fragment>
          )
        }

        return (
          <StateProvider>{(isExpanded, setExpanded) => {
            const optionsClass = classNames(
              'options-wrapper',
              isExpanded && 'is-expanded'
            )

            const handleFocus = (e) => {
              e.preventDefault()
              setExpanded(true)
            }

            const handleBlur = () => {
              input.onFocus()
              input.onBlur()
              setExpanded(false)
            }

            return (
              <div styleName={`${styleName}`}>
                <svg viewBox="0 0 30 18"><path d="M2,2 L15,14 L28,2" /></svg>
                <label>{label}</label>
                <select 
                  name={name}
                  value={input.value}
                  onChange={input.onChange}
                  {...rest}
                >
                  <option disabled value=""></option>
                  {renderSelectOptions()}
                </select>
                <input 
                  // name={name}
                  value={input.value}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  readOnly
                  {...rest}
                />
                {hasError && <span data-error styleName="error">{meta.error}</span>}
    
                <div styleName={`${optionsClass}`}>
                  <h6>{label}</h6>
                  <div styleName='options'>
                    {renderOptions()}
                  </div>
                </div>
              </div>
            )
          }}</StateProvider>
        )
      }}
    />
  )
}


export const TextareaField = ({ 
  name, 
  label, 
  hasValidationErrors,
  ...fieldRest 
}) => (
  <RefProvider>{ref => (
    <StateProvider>{(lastVal, setLastVal) => (
      <Field 
        name={name} 
        render={({ meta, input }) => {
          const styleName = classNames(
            'textarea-field',
            (meta.active || input.value) && 'is-active'
          )

          const { onChange, value, ...rest } = input

          const handleChange = (e) => {
            const lineCount = e.target.value.split('\n').length;

            if (lineCount >= 5) {
              e.target.value = value
              return
            }

            onChange(e)
          }

          const handleKeyDown = (e) => {
            setLastVal(e.target.value)
          }

          const handleScroll = (e) => {
            if (e.target.scrollTop !== 0) {
              e.target.value = lastVal
              ref.current.scrollTo(0,0)
            }
          }

          return (
            <div styleName={`${styleName}`}>
              <label>{label}</label>
              <textarea 
                ref={ref}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onScroll={handleScroll}
                {...rest}
                {...fieldRest}
                value={value}
              ></textarea>
              <div styleName="underlines">
                <div styleName="underline"/>
                <div styleName="underline"/>
                <div styleName="underline"/>
              </div>
            </div>
          )
        }}
      />
    )}</StateProvider>
  )}</RefProvider>
)